import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Renders when the URL is /auth/square/callback?code=...&state=...
//
// Flow:
//   1. Square redirects the user here after they consent on Square's UI.
//   2. We extract code + state from the URL params.
//   3. Decode environment from the state suffix (we set it to "uuid:env" upstream).
//   4. Look up the user's active restaurant (RLS-scoped — they only see their own).
//   5. POST to the square-oauth edge function with kind='callback' to exchange
//      the code for tokens, store on the restaurant row, return success.
//   6. Show "Connected!" and route the user back to Settings.
//
// Errors at any step show a friendly message + a Retry button that takes
// them back to Settings to start over.
export default function SquareCallbackHandler() {
  const [phase, setPhase]   = useState('working') // working | done | error
  const [message, setMessage] = useState('Finishing the Square connection…')

  useEffect(() => {
    let mounted = true

    async function run() {
      try {
        const params = new URLSearchParams(window.location.search)
        const code   = params.get('code')
        const state  = params.get('state')
        const error  = params.get('error')
        const errDescription = params.get('error_description')

        // The merchant denied consent or Square returned an error.
        if (error) {
          throw new Error(errDescription || `Square returned: ${error}`)
        }
        if (!code || !state) {
          throw new Error('Square redirect was missing code or state. Try again.')
        }

        // Decode environment from state ("uuid:environment").
        const envFromState = state.includes(':') ? state.split(':')[1] : 'production'

        // Find the user's restaurant. RLS limits this to their own membership.
        const { data: rest, error: restErr } = await supabase
          .from('restaurants')
          .select('id')
          .eq('active', true)
          .order('created_at')
          .limit(1)
          .maybeSingle()
        if (restErr) throw new Error(restErr.message)
        if (!rest?.id) throw new Error('No active restaurant found for your account.')

        // Exchange code for tokens.
        const { data, error: fnErr } = await supabase.functions.invoke('square-oauth', {
          body: {
            restaurant_id: rest.id,
            kind:          'callback',
            code,
            state,
            environment:   envFromState,
          },
        })
        if (fnErr) throw fnErr
        if (!data?.ok) {
          throw new Error(data?.message || 'Could not complete Square connection.')
        }

        if (!mounted) return

        // The SERVER resolved the initiating restaurant by OAuth state —
        // trust data.restaurant_id over our best-effort hint above (the
        // hint guesses the user's first active unit, which is wrong when
        // they own several). Scope the app to the RESOLVED unit so the
        // location picker runs on mount in its Settings → Square section.
        const resolvedId = data.restaurant_id || rest.id
        try {
          if (resolvedId) window.localStorage.setItem('armCurrentUnitId', resolvedId)
        } catch (_e) { /* localStorage unavailable — non-fatal */ }

        setPhase('done')
        setMessage(`Connected to Square (merchant ${data.merchant_id || 'unknown'}). Redirecting to finish setup…`)

        // Briefly show success, then bounce to the app scoped to the
        // resolved unit, where the operator picks this unit's location.
        setTimeout(() => { window.location.href = '/?square_connected=1' }, 1500)
      } catch (e) {
        if (!mounted) return
        // eslint-disable-next-line no-console
        console.error('[SquareCallbackHandler] failed', e)
        setPhase('error')
        setMessage(e?.message || 'Something went wrong.')
      }
    }

    run()
    return () => { mounted = false }
  }, [])

  return (
    <div className="min-h-full bg-white">
      <header className="bg-amber-brand-50 text-amber-brand-800 safe-top">
        <div className="mx-auto max-w-screen-sm px-4 py-4 flex items-center gap-3">
          <img
            src="/logo.png"
            alt=""
            className="h-10 w-10 rounded-lg shadow-md flex-shrink-0"
          />
          <div className="text-xs font-medium opacity-90">
            AI Restaurant Manager
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-screen-sm px-4 py-12 text-center">
        {phase === 'working' && (
          <>
            <svg className="mx-auto h-8 w-8 animate-spin text-blue-600" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
              <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
            </svg>
            <p className="mt-4 text-base text-black/75">{message}</p>
          </>
        )}

        {phase === 'done' && (
          <>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-2xl">
              ✓
            </div>
            <p className="mt-4 text-lg font-medium text-emerald-900">Square connected</p>
            <p className="mt-1 text-sm text-black/70">{message}</p>
          </>
        )}

        {phase === 'error' && (
          <>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-2xl">
              ⚠
            </div>
            <p className="mt-4 text-lg font-medium text-red-900">Couldn't connect to Square</p>
            <p className="mt-1 text-sm text-black/70">{message}</p>
            <button
              type="button"
              onClick={() => { window.location.href = '/' }}
              className="mt-6 rounded-lg bg-blue-600 px-5 py-3 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
            >
              Back to Settings
            </button>
          </>
        )}
      </main>
    </div>
  )
}
